import React from "react";

// ── Config ────────────────────────────────────────────────────────────────────
const COUNTER_DURATION_MS = 3 * 60 * 1000; // 15s for testing — change to 3 * 60 * 1000 for conference
const FETCH_INTERVAL_MS   = 4000;     // poll every 4s

// Path to your demo video — put it in /public/demo.mp4
const VIDEO_SRC = "/demo.mp4";

// ── Booth counter API helpers ─────────────────────────────────────────────────
const BOOTH_API = (import.meta.env.VITE_API_BASE_URL || "") + "/public/booth-counter";

async function getBoothCounter() {
  try {
    const res = await fetch(BOOTH_API);
    if (!res.ok) return { total: 0, count: 0 };
    const data = await res.json();
    return { total: data.total_sats || 0, count: data.demo_count || 0 };
  } catch { return { total: 0, count: 0 }; }
}

// ── Counter animation hook ────────────────────────────────────────────────────
function useCountUp(target, duration = 2000) {
  const [value, setValue] = React.useState(0);
  const raf      = React.useRef(null);
  const fromRef  = React.useRef(0);
  const startRef = React.useRef(null);

  React.useEffect(() => {
    if (target === fromRef.current) return;
    const start = fromRef.current;
    const diff  = target - start;
    startRef.current = null;

    const ms = Math.min(Math.max(Math.abs(diff) * 0.4, 400), duration);

    const animate = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const p     = Math.min((ts - startRef.current) / ms, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      const cur   = Math.round(start + diff * eased);
      setValue(cur);
      if (p < 1) {
        raf.current = requestAnimationFrame(animate);
      } else {
        fromRef.current = target;
      }
    };

    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(animate);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target]);

  return value;
}

// ── Scenes ────────────────────────────────────────────────────────────────────
const SCENE = { COUNTER: "counter", VIDEO: "video" };

export default function BoothDisplay() {
  const [scene,       setScene]       = React.useState(SCENE.COUNTER);
  const [visible,     setVisible]     = React.useState(true); // for crossfade
  const [total,       setTotal]       = React.useState(0);
  const [demoCount,   setDemoCount]   = React.useState(0);
  const videoRef    = React.useRef(null);
  const pollRef     = React.useRef(null);
  const sceneTimer  = React.useRef(null);
  const animated    = useCountUp(total);

  // ── Poll API ────────────────────────────────────────────────────────────────
  const refresh = React.useCallback(async () => {
    const { total, count } = await getBoothCounter();
    setTotal(total);
    setDemoCount(count);
  }, []);

  const startPolling = React.useCallback(() => {
    refresh();
    pollRef.current = setInterval(refresh, FETCH_INTERVAL_MS);
  }, [refresh]);

  const stopPolling = React.useCallback(() => {
    clearInterval(pollRef.current);
  }, []);

  // ── Scene switching with crossfade ─────────────────────────────────────────
  const switchTo = React.useCallback((next) => {
    setVisible(false);
    setTimeout(() => {
      setScene(next);
      setVisible(true);
    }, 1000); // fade duration
  }, []);

  const goToCounter = React.useCallback(() => {
    startPolling();
    switchTo(SCENE.COUNTER);
    sceneTimer.current = setTimeout(() => {
      stopPolling();
      goToVideo();
    }, COUNTER_DURATION_MS);
  }, [startPolling, stopPolling, switchTo]);

  const goToVideo = React.useCallback(() => {
    switchTo(SCENE.VIDEO);
    // video play is triggered in useEffect below when scene === VIDEO
  }, [switchTo]);

  // ── Init ───────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    goToCounter();
    return () => {
      clearTimeout(sceneTimer.current);
      clearInterval(pollRef.current);
    };
  }, []);

  // ── Play video when scene switches to VIDEO ─────────────────────────────────
  React.useEffect(() => {
    if (scene !== SCENE.VIDEO) return;
    const vid = videoRef.current;
    if (!vid) return;
    vid.currentTime = 0;
    vid.play().catch(() => {
      // Autoplay blocked or video missing — go back to counter
      goToCounter();
    });
  }, [scene]);

  const handleVideoEnd = () => {
    goToCounter();
  };

  const handleVideoError = () => {
    goToCounter();
  };

  return (
    <div style={styles.root}>
      {/* Google Font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;600;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes pulse-dot {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(249,115,22,0.6); }
          50%       { opacity: 0.7; box-shadow: 0 0 0 8px rgba(249,115,22,0); }
        }
        @keyframes flicker {
          0%, 100% { opacity: 1; }
          92%       { opacity: 1; }
          93%       { opacity: 0.85; }
          94%       { opacity: 1; }
          96%       { opacity: 0.9; }
          97%       { opacity: 1; }
        }
        @keyframes scanline {
          0%   { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
      `}</style>

      {/* Scanline effect */}
      <div style={styles.scanline} />

      {/* Grain overlay */}
      <div style={styles.grain} />

      {/* ── Counter scene ── */}
      <div style={{
        ...styles.scene,
        opacity: scene === SCENE.COUNTER && visible ? 1 : 0,
        pointerEvents: scene === SCENE.COUNTER ? "auto" : "none",
      }}>
        {/* Ambient glow */}
        <div style={styles.glow} />

        {/* Top bar */}
        <div style={styles.topBar}>
          <div style={styles.badge}>
            <span style={styles.badgeDot} />
            <span style={styles.badgeText}>Bitcoin 2026 · Las Vegas · Booth #3391</span>
          </div>
          <div style={styles.wordmark}>
            <span style={styles.wordmarkDot} />
            <span style={styles.wordmarkText}>SatStacker</span>
            <span style={styles.wordmarkUrl}>satstacker.app</span>
          </div>
        </div>

        {/* Main content */}
        <div style={styles.counterBody}>
          <div style={styles.counterLabel}>
            sats left on the table at Bitcoin 2026
          </div>

          <div style={styles.counterNumber}>
            {animated.toLocaleString()}
          </div>

          <div style={styles.counterUnit}>SATS</div>

          <div style={styles.divider} />

          <div style={styles.headline}>
            Come talk to us and find out<br />
            <span style={styles.headlineAccent}>how many you’re missing.</span>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={styles.bottomBar}>
          {demoCount > 0 && (
            <span style={styles.demoCount}>
              {demoCount} demo{demoCount !== 1 ? "s" : ""} run
            </span>
          )}
          <span style={styles.cta}>
            Visit our team to calculate yours
          </span>
        </div>
      </div>

      {/* ── Video scene ── */}
      <div style={{
        ...styles.scene,
        background: "#000",
        opacity: scene === SCENE.VIDEO && visible ? 1 : 0,
        pointerEvents: scene === SCENE.VIDEO ? "auto" : "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <video
          ref={videoRef}
          src={VIDEO_SRC}
          muted
          playsInline
          preload="auto"
          onEnded={handleVideoEnd}
          onError={handleVideoError}
          style={styles.video}
        />
        {/* Vignette */}
        <div style={styles.vignette} />

        {/* Watermark on video */}
        <div style={styles.videoWatermark}>
          <span style={styles.wordmarkDot} />
          <span style={{ ...styles.wordmarkText, fontSize: "1.4rem" }}>SatStacker</span>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  root: {
    width: "100vw",
    height: "100vh",
    background: "#080706",
    overflow: "hidden",
    position: "relative",
    fontFamily: "'DM Sans', sans-serif",
    WebkitFontSmoothing: "antialiased",
    animation: "flicker 8s infinite",
  },
  scanline: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: "rgba(249,115,22,0.03)",
    animation: "scanline 12s linear infinite",
    zIndex: 10,
    pointerEvents: "none",
  },
  grain: {
    position: "absolute",
    inset: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
    pointerEvents: "none",
    zIndex: 9,
    opacity: 0.5,
  },
  scene: {
    position: "absolute",
    inset: 0,
    transition: "opacity 1s ease",
    display: "flex",
    flexDirection: "column",
  },
  glow: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "70vw",
    height: "60vh",
    background: "radial-gradient(ellipse, rgba(249,115,22,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "40px 80px 0",
    flexShrink: 0,
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 12,
    background: "#111110",
    border: "1px solid #1f1e1c",
    borderRadius: 999,
    padding: "10px 28px",
  },
  badgeDot: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#f97316",
    animation: "pulse-dot 2s ease-in-out infinite",
    flexShrink: 0,
  },
  badgeText: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#78716c",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  wordmark: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  wordmarkDot: {
    display: "inline-block",
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: "#f97316",
    flexShrink: 0,
  },
  wordmarkText: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: "1.8rem",
    letterSpacing: "0.08em",
    color: "#fafaf9",
  },
  wordmarkUrl: {
    fontSize: "1rem",
    color: "#57534e",
    letterSpacing: "0.04em",
    marginLeft: 4,
  },
  counterBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 80px",
    position: "relative",
    zIndex: 1,
  },
  counterLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "1.3rem",
    fontWeight: 500,
    color: "#57534e",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    marginBottom: 16,
    textAlign: "center",
  },
  counterNumber: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: "clamp(4rem, 18vw, 18rem)",
    lineHeight: 0.85,
    color: "#f97316",
    letterSpacing: "-0.01em",
    textAlign: "center",
    textShadow: "0 0 80px rgba(249,115,22,0.25), 0 0 200px rgba(249,115,22,0.08)",
    marginBottom: 16,
    maxWidth: "90vw",
    wordBreak: "break-all",
  },
  counterUnit: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "2rem",
    fontWeight: 500,
    color: "#44403c",
    letterSpacing: "0.3em",
    textAlign: "center",
    marginBottom: 48,
  },
  divider: {
    width: 80,
    height: 2,
    background: "linear-gradient(90deg, transparent, #f97316, transparent)",
    marginBottom: 48,
  },
  headline: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "2.6rem",
    fontWeight: 800,
    color: "#e7e5e4",
    textAlign: "center",
    lineHeight: 1.3,
  },
  headlineAccent: {
    color: "#f97316",
  },
  bottomBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 80px 40px",
    flexShrink: 0,
  },
  demoCount: {
    fontFamily: "'DM Mono', monospace",
    fontSize: "1rem",
    color: "#292524",
    letterSpacing: "0.08em",
  },
  cta: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#44403c",
    letterSpacing: "0.04em",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  vignette: {
    position: "absolute",
    inset: 0,
    background: "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.6) 100%)",
    pointerEvents: "none",
  },
  videoWatermark: {
    position: "absolute",
    bottom: 40,
    right: 80,
    display: "flex",
    alignItems: "center",
    gap: 10,
    opacity: 0.4,
  },
};
